/**
 * Small DSP / music-theory helpers shared by sfx, music and voice.
 * Everything here is pure (no AudioContext) except the buffer builders.
 */

export const TAU = Math.PI * 2;

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const mtof = (midi: number): number => 440 * Math.pow(2, (midi - 69) / 12);
export const dbToGain = (db: number): number => Math.pow(10, db / 20);

/** Cheap seeded RNG (mulberry32) so procedural music is reproducible per theme. */
export class Rand {
  private s: number;
  constructor(seed = 1) {
    this.s = seed >>> 0 || 1;
  }
  next(): number {
    let t = (this.s += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number): number {
    return a + (b - a) * this.next();
  }
  int(a: number, b: number): number {
    return Math.floor(this.range(a, b + 1));
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  chance(p: number): boolean {
    return this.next() < p;
  }
  fork(salt: number): Rand {
    return new Rand((this.s ^ Math.imul(salt + 1, 0x9e3779b1)) >>> 0);
  }
}

export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Unseeded helpers for sfx variation. */
export const rnd = (a = 0, b = 1): number => a + Math.random() * (b - a);
export const rndSign = (): number => (Math.random() < 0.5 ? -1 : 1);
/** Multiply by a random ratio in ±cents (musical variation). */
export const cents = (c: number): number => Math.pow(2, ((Math.random() * 2 - 1) * c) / 1200);

// ---------------------------------------------------------------------------
// Scales & chords (semitone offsets from root)
// ---------------------------------------------------------------------------

export const SCALES = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  phrygianDominant: [0, 1, 4, 5, 7, 8, 10],
  majorPent: [0, 2, 4, 7, 9],
  minorPent: [0, 3, 5, 7, 10],
  wholeTone: [0, 2, 4, 6, 8, 10],
} as const;
export type ScaleName = keyof typeof SCALES;

/** Scale degree (can exceed length / be negative) → semitone offset from root. */
export function degreeToSemis(scale: readonly number[], degree: number): number {
  const n = scale.length;
  const oct = Math.floor(degree / n);
  const idx = ((degree % n) + n) % n;
  return scale[idx] + oct * 12;
}

/** Chord tones (as scale degrees) stacked in thirds on a degree. */
export function chordDegrees(degree: number, size = 3): number[] {
  const out: number[] = [];
  for (let i = 0; i < size; i++) out.push(degree + i * 2);
  return out;
}

// ---------------------------------------------------------------------------
// Buffers
// ---------------------------------------------------------------------------

export type NoiseColor = 'white' | 'pink' | 'brown';

export function makeNoiseBuffer(ctx: BaseAudioContext, seconds: number, color: NoiseColor, channels = 1): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(channels, len, ctx.sampleRate);
  for (let c = 0; c < channels; c++) {
    const d = buf.getChannelData(c);
    if (color === 'white') {
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    } else if (color === 'pink') {
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
      for (let i = 0; i < len; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + w * 0.0555179;
        b1 = 0.99332 * b1 + w * 0.0750759;
        b2 = 0.969 * b2 + w * 0.153852;
        b3 = 0.8665 * b3 + w * 0.3104856;
        b4 = 0.55 * b4 + w * 0.5329522;
        b5 = -0.7616 * b5 - w * 0.016898;
        d[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    } else {
      let last = 0;
      for (let i = 0; i < len; i++) {
        const w = Math.random() * 2 - 1;
        last = (last + 0.02 * w) / 1.02;
        d[i] = last * 3.5;
      }
    }
  }
  return buf;
}

/** Generated stereo impulse response: exponentially decaying noise with a little early-reflection bite. */
export function makeImpulseResponse(ctx: BaseAudioContext, seconds: number, decay: number, brightness = 0.6): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const t = i / len;
      const env = Math.pow(1 - t, decay);
      const w = Math.random() * 2 - 1;
      lp += (w - lp) * brightness;
      // a few sparse early reflections in the first 60ms
      const early = i < ctx.sampleRate * 0.06 && Math.random() < 0.002 ? (Math.random() * 2 - 1) * 0.6 : 0;
      d[i] = (lp * env + early) * (1 - Math.exp(-i / 200));
    }
  }
  return buf;
}

/** Soft-clip curve for a WaveShaper (drive 1..∞). */
export function makeSoftClipCurve(drive = 2, n = 1024): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * drive) / Math.tanh(drive);
  }
  return curve;
}

/**
 * Master soft clipper for a WaveShaper fed at half gain: input x∈[-1,1] represents ±2 (6 dB
 * headroom). Unity slope at 0, smooth tanh knee, ceiling 0.96 (≈ -0.35 dBFS).
 */
export function makeHeadroomClipCurve(n = 2048): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(n);
  const ceil = 0.96;
  for (let i = 0; i < n; i++) {
    const u = ((i / (n - 1)) * 2 - 1) * 2;
    curve[i] = ceil * Math.tanh(u / ceil);
  }
  return curve;
}
